import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Loader2, X, Save, ArrowLeft, Undo2, TrendingUp, Link2,
} from 'lucide-react';
import { priceIndexService } from '../../../services/priceIndexService';
import { isAdmin } from '../../../utils/permissions';
import { useAppContext } from '../../../context/AppContext';
import MantleDataGrid, { gcell } from '../../../components/MantleDataGrid';
import { PERIODS } from '../../../utils/financialPeriods';
import usePeriodDrilldown, { periodColumns, periodsOfCol, periodHeaderToggle, aggPeriods } from '../../../hooks/usePeriodDrilldown';

// =============================================================================
// Budget Setup > Price Indices  (Distribution)
//
// ONE ENTRY, EVERY RATE MOVES.
//
// Diesel goes up and down and every transport rate in the business moves with
// it. The only way to say so used to be retyping twelve monthly rates on every
// rate line — 40 lanes x 3 carriers x 2 components x 12 months is 2 880 numbers
// because diesel moved 50c. So nobody re-costs, and the LE is wrong.
//
// THE MECHANISM IS A SHARE, NOT A LITRE CALCULATION. You cannot turn "R450 a
// drop" into litres; there is no honest consumption figure inside a contracted
// quote. So the rate line says what PROPORTION of itself is diesel:
//
//     adjusted rate = rate x ( 1 + share% x ( now / base - 1 ) )
//
// Diesel R22.50 -> R25.00 is +11.1%. Per-km at 40% moves +4.4%, per-drop at 20%
// moves +2.2%, per-pallet at 10% moves +1.1%. The rate BASIS stops mattering,
// which is the whole point — one arithmetic for per-km, per-drop, per-run,
// per-trip and per-pallet alike.
//
// The base each rate escalates from lives on the RATE LINE, not here: carrier A
// quoted in January at R22.50 and carrier B in March at R25.00 must not share
// one base, or B is escalated for a rise already inside its price. What is here
// is the DEFAULT a new link is stamped with.
//
// A TWELVE-PERIOD GRID, on the same engine as every other budget grid in
// Mantle. A diesel curve arrives as a row of twelve numbers out of somebody's
// spreadsheet, and pasting it in one go is the actual workflow — which is what
// MantleDataGrid gives for free, along with the fill handle, Ctrl+D/R and
// Ctrl+F. Twelve <input> boxes gave none of it.
//
// SAVE BUTTON, not autosave. A cell in this grid reprices every lane in
// inter-depot and secondary at once — that is not an edit that should commit
// because focus moved.
// =============================================================================

const BLANK = { index_code: '', index_name: '', uom: '', default_base_value: 0, is_active: true, notes: '' };

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const field = 'w-full text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-40';
const numField = `${field} text-right font-mono`;
const th = 'px-2 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap';

const fmtR = (n, dp = 0) => `R ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const fmt2 = (n) => Number(n || 0).toFixed(2);
const fmtPct = (n) => `${n >= 0 ? '+' : ''}${Number(n || 0).toFixed(1)}%`;

// Canvas cells carry colour as a theme override, not a CSS class. Movement is
// the one thing on this grid that has to read at a glance, so it is the one
// thing that gets colour.
const UP = { textDark: '#dc2626' };
const DOWN = { textDark: '#059669' };
const FLAT = { textDark: '#94a3b8' };
const WARN = { textDark: '#d97706' };
const MUTED = { textDark: '#94a3b8' };

export default function PriceIndices() {
  const navigate = useNavigate();
  const { selectedRole, selectedVersion, fyStartMonth } = useAppContext();
  const canEditMaster = isAdmin(selectedRole);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [dirtyValues, setDirtyValues] = useState({});   // index_code -> true
  const [dirtyMaster, setDirtyMaster] = useState({});
  const [entry, setEntry] = useState(BLANK);
  const [adding, setAdding] = useState(false);
  const [whatIf, setWhatIf] = useState({});             // index_code -> typed value

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await priceIndexService.getIndices();
      setRows((res.data?.indices || []).map((i) => ({ ...i, values: [...(i.values || Array(12).fill(0))] })));
      setDirtyValues({});
      setDirtyMaster({});
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the price indices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const patchMaster = useCallback((code, p) => {
    setRows((prev) => prev.map((r) => (r.index_code === code ? { ...r, ...p } : r)));
    setDirtyMaster((d) => ({ ...d, [code]: true }));
  }, []);

  const setValue = useCallback((code, period, raw) => {
    const n = Math.max(0, Number(raw) || 0);
    setRows((prev) => prev.map((r) => {
      if (r.index_code !== code) return r;
      const next = [...r.values];
      next[period - 1] = n;
      return { ...r, values: next };
    }));
    setDirtyValues((d) => ({ ...d, [code]: true }));
  }, []);

  const hasChanges = Object.keys(dirtyValues).length > 0 || Object.keys(dirtyMaster).length > 0;

  // ---- Grid -----------------------------------------------------------------
  // Periods drill down — Full Year → quarters → months, same as OPEX entry. An
  // index level is a PRICE, so a group column is the MEAN over those months:
  // the sum of twelve diesel prices is not a number anybody uses.
  const drill = usePeriodDrilldown(PERIODS);

  const gcols = useMemo(() => ([
    { id: 'index_name', title: 'Index', width: 150 },
    { id: 'index_code', title: 'Code', width: 88 },
    { id: 'uom', title: 'Unit', width: 84 },
    { id: 'default_base_value', title: 'Base', width: 78 },
    ...periodColumns(drill, fyStartMonth, { monthWidth: 76, groupWidth: 112, agg: 'avg' }),
    { id: 'avg', title: 'Average', width: 84 },
    { id: 'drift', title: 'vs Base', width: 78 },
    { id: 'linked', title: 'Linked', width: 70 },
    { id: 'notes', title: 'Notes', width: 260 },
    { id: 'del', title: '', width: 44 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [fyStartMonth, drill.visibleCols, drill.yearExpanded, drill.expandedQuarters]);

  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const derived = useMemo(() => rows.map((r) => {
    const base = Number(r.default_base_value) || 0;
    const avg = r.values.reduce((a, v) => a + (Number(v) || 0), 0) / 12;
    return { base, avg, drift: base > 0 ? ((avg / base) - 1) * 100 : 0 };
  }), [rows]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = rows[row];
    const d = derived[row];
    if (!r) return gcell.text('', { readonly: true });

    const ps = periodsOfCol(gcols, id);
    if (ps) {
      // A single month is the price you type. A quarter or the year is the MEAN
      // price over those months — read-only, because there is no such thing as
      // typing one number into three months of a published index.
      const v = aggPeriods(ps, (p) => r.values[p - 1], 'avg');
      // Colour the PRICE by how it sits against its own base, not the cell
      // beside it. The escalation is driven by that ratio, so showing the ratio
      // is showing the mechanism instead of asking anyone to trust it.
      const pct = d.base > 0 ? ((v / d.base) - 1) * 100 : 0;
      const theme = Math.abs(pct) < 0.05 ? undefined : (pct > 0 ? UP : DOWN);
      return gcell.number(v, { readonly: ps.length > 1, display: fmt2(v), theme });
    }

    switch (id) {
      case 'index_name': return gcell.text(r.index_name, { readonly: !canEditMaster });
      case 'index_code': return gcell.text(r.index_code, { readonly: true, theme: MUTED });
      case 'uom': return gcell.text(r.uom || '', { readonly: !canEditMaster });
      case 'default_base_value':
        return gcell.number(d.base, { display: fmt2(d.base), readonly: !canEditMaster });
      case 'avg': return gcell.number(d.avg, { display: fmt2(d.avg), readonly: true, theme: MUTED });
      case 'drift':
        return gcell.text(fmtPct(d.drift), {
          readonly: true,
          theme: Math.abs(d.drift) < 0.05 ? FLAT : (d.drift > 0 ? UP : DOWN),
        });
      // Zero linked lines is the commonest reason for "I changed diesel and
      // nothing moved", so it is flagged rather than left as a quiet 0.
      case 'linked':
        return gcell.number(r.linked_lines || 0, { readonly: true, theme: r.linked_lines ? undefined : WARN });
      case 'notes': return gcell.text(r.notes || '', { readonly: !canEditMaster });
      // No ✕ on a linked index, and none at all without the role to use it —
      // a control that does nothing when clicked is worse than no control.
      case 'del': return gcell.text((canEditMaster && !r.linked_lines) ? '✕' : '', { readonly: true, theme: MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, [colIds, gcols, rows, derived, canEditMaster]);

  const onCellEdited = useCallback(([col, row], newValue) => {
    const id = colIds[col];
    const r = rows[row];
    if (!r) return;

    // Months only: a quarter column is a mean and writing it back would flatten
    // three published prices into one.
    const ps = periodsOfCol(gcols, id);
    if (ps) { if (ps.length === 1) setValue(r.index_code, ps[0], newValue.data); return; }
    switch (id) {
      case 'index_name': patchMaster(r.index_code, { index_name: String(newValue.data ?? '').trim() }); break;
      case 'uom': patchMaster(r.index_code, { uom: String(newValue.data ?? '').trim() }); break;
      case 'default_base_value': patchMaster(r.index_code, { default_base_value: Math.max(0, Number(newValue.data) || 0) }); break;
      case 'notes': patchMaster(r.index_code, { notes: String(newValue.data ?? '') }); break;
      default: break;
    }
  }, [colIds, gcols, rows, setValue, patchMaster]);

  const onCellClicked = useCallback(([col, row]) => {
    if (colIds[col] !== 'del' || !canEditMaster) return;
    const r = rows[row];
    // Linked indices show no ✕ at all — deleting one would unlink live rate
    // cards, and every one of them would quietly stop escalating.
    if (!r || r.linked_lines) return;
    setBusy(true);
    setError('');
    priceIndexService.deleteIndex(r.index_code)
      .then((res) => { setToast(res.message || 'Removed.'); return load(); })
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setBusy(false));
  }, [colIds, rows, canEditMaster, load]);

  // ---- Writes ---------------------------------------------------------------
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const masterRows = rows.filter((r) => dirtyMaster[r.index_code]);
      const bad = masterRows.find((r) => !(Number(r.default_base_value) > 0));
      if (bad) {
        setError(`${bad.index_code}: the base must be greater than zero — every rate linked to it divides by this number.`);
        setBusy(false);
        return;
      }
      if (masterRows.length) await priceIndexService.saveIndices(masterRows);
      const valueRows = rows.filter((r) => dirtyValues[r.index_code])
        .map((r) => ({ index_code: r.index_code, values: r.values }));
      if (valueRows.length) await priceIndexService.saveValues(valueRows);
      setToast('Saved. Every linked rate in inter-depot and secondary has moved with it.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!entry.index_code.trim() || !entry.index_name.trim()) {
      setError('A code and a name are both required.');
      return;
    }
    if (!(Number(entry.default_base_value) > 0)) {
      setError('The base must be greater than zero — every rate linked to this index divides by it.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const code = entry.index_code.trim().toUpperCase();
      await priceIndexService.saveIndices([{ ...entry, index_code: code }]);
      // A brand-new index with no monthly values would read as zero everywhere
      // and escalate nothing. Seed the year flat ON the base, so it is inert
      // until somebody deliberately moves a month.
      await priceIndexService.saveValues([{ index_code: code, values: Array(12).fill(Number(entry.default_base_value)) }]);
      setToast(`${code} added, flat at its base — nothing has changed until you move a month.`);
      setEntry(BLANK);
      setAdding(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runWhatIf = async (code) => {
    const v = Number(whatIf[code]);
    if (!Number.isFinite(v) || v < 0) { setError('Type a price to test.'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await priceIndexService.getImpact(code, v);
      const d = res.data || {};
      const delta = Number(d.delta) || 0;
      setToast(
        `At ${fmt2(v)} for the whole year, distribution ${delta >= 0 ? 'costs' : 'saves'} ${fmtR(Math.abs(delta))} — `
        + `${fmtR(d.budget_amount)} becomes ${fmtR(d.projected_amount)}.`
      );
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const totals = useMemo(() => rows.reduce((a, r) => ({
    linked: a.linked + (r.linked_lines || 0),
    adjustment: a.adjustment + (r.impact?.index_adjustment || 0),
  }), { linked: 0, adjustment: 0 }), [rows]);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <button className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={() => navigate('/distribution')}>
          <ArrowLeft className="w-3.5 h-3.5" /> Distribution
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Price Indices</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              The volatile prices that move rates across the whole of distribution. Change diesel in one cell and every linked rate
              in inter-depot and secondary moves with it — each in proportion to how much of that rate is actually diesel.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canEditMaster && (
              <button className={btn} onClick={() => setAdding((a) => !a)} disabled={busy}>
                <Plus className="w-3.5 h-3.5" /> Add index
              </button>
            )}
            <button className={btn} onClick={load} disabled={!hasChanges || busy}><Undo2 className="w-3.5 h-3.5" /> Discard</button>
            <button className={btnPrimary} onClick={save} disabled={busy || !hasChanges}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save{hasChanges ? ` (${new Set([...Object.keys(dirtyValues), ...Object.keys(dirtyMaster)]).size})` : ''}
            </button>
          </div>
        </div>

        {!canEditMaster && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              The monthly prices are yours to change — revising diesel for an LE is planning work. Creating or renaming an index
              needs an administrative role, because the index itself is shared by every budget version.
            </p>
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {toast && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <p className="m-0 text-xs text-emerald-700 dark:text-emerald-300">{toast}</p>
          </div>
        )}

        {adding && canEditMaster && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800">
            {[
              ['index_code', 'Code', 'DIESEL'],
              ['index_name', 'Name', 'Diesel'],
              ['uom', 'Unit', 'R / litre'],
            ].map(([k, label, ph]) => (
              <div key={k}>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</label>
                <input className={field} placeholder={ph} value={entry[k]}
                       onChange={(e) => setEntry((s) => ({ ...s, [k]: e.target.value }))} />
              </div>
            ))}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Default base</label>
              <input className={numField} type="number" min="0" step="0.01" placeholder="22.50"
                     value={entry.default_base_value}
                     onChange={(e) => setEntry((s) => ({ ...s, default_base_value: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <button className={btnPrimary} onClick={add} disabled={busy}>Add</button>
              <button className={btn} onClick={() => { setAdding(false); setEntry(BLANK); }}>Cancel</button>
            </div>
            <p className="col-span-full m-0 text-[11px] text-slate-400">
              <strong>Unit</strong> is documentation only — the escalation is a ratio, so R/litre and c/kWh take identical arithmetic and nothing is ever converted.
              <strong> Default base</strong> is the price a new rate link is stamped with, roughly what the current rate cards were quoted at.
            </p>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-4">
        {/* The twelve periods, on the same grid engine as every other budget
            grid: paste a diesel curve straight out of a spreadsheet, drag the
            fill handle, Ctrl+R across the rest of the year. */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 max-h-full flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              No indices yet. Add diesel first — it is the one that moves everything.
            </p>
          ) : (
            <MantleDataGrid
              gridId="dist-price-indices"
              fitHeight
              fitHeightPadding={6}
              columns={gcols}
              rowCount={rows.length}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              onHeaderClicked={(col) => periodHeaderToggle(drill, colIds[col])}
              freezeColumns={2}
              hint="Click Full Year → quarters → months to drill in; a group column is the AVERAGE price over those months. Type a month, or paste twelve straight from a spreadsheet. Drag the fill handle or Ctrl+R to carry a price across the rest of the year. Colour is the price against its own base — that ratio is what drives the escalation. Nothing is written until you press Save."
              toolbar={
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {rows.length} {rows.length === 1 ? 'index' : 'indices'} · {totals.linked} rate line{totals.linked === 1 ? '' : 's'} linked
                  {hasChanges && ' · unsaved changes'}
                </span>
              }
            />
          )}
        </div>

        {/* What each index has already done to the budget, and what one more
            unit would do. Read-only arithmetic off the cost views — not grid
            data, so not in the grid. */}
        {!loading && rows.length > 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-slate-100 dark:bg-slate-700/60 text-left">
                  {['Index', 'Linked spend / yr', 'Of which index movement', 'Sensitivity', 'What if it were', ''].map((h) => (
                    <th key={h} className={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const adj = r.impact?.index_adjustment || 0;
                  return (
                    <tr key={r.index_code} className={`border-t border-slate-100 dark:border-slate-700/60 ${i % 2 ? 'bg-slate-50/50 dark:bg-slate-800/40' : ''}`}>
                      <td className="px-2 py-1.5 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                        {r.index_name}
                        {!r.linked_lines && (
                          <span className="ml-1.5 text-[10px] font-normal text-amber-600 dark:text-amber-400"
                                title="Nothing links to this index, so changing it moves no cost. Link it on a rate line under Inter-Depot Transport or Secondary Lanes & Rates.">
                            <Link2 className="inline w-3 h-3 mr-0.5" />nothing linked
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                        {fmtR(r.impact?.budget_amount)}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-mono font-semibold whitespace-nowrap ${adj > 0
                        ? 'text-red-600 dark:text-red-400' : adj < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}
                          title="What the index movement has already added to (or taken off) the budget, versus every line sitting at its own quoted base.">
                        {fmtR(adj)}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        +1{r.uom ? ` ${r.uom.replace(/^R\s*\/\s*/i, '/ ')}` : ''} = {fmtR(r.impact?.sensitivity_per_unit)}
                      </td>
                      <td className="px-2 py-1.5">
                        <input className={numField} style={{ width: 90 }} type="number" min="0" step="0.01"
                               placeholder={fmt2(r.default_base_value)}
                               value={whatIf[r.index_code] ?? ''}
                               onChange={(e) => setWhatIf((w) => ({ ...w, [r.index_code]: e.target.value }))} />
                      </td>
                      <td className="px-2 py-1.5">
                        <button className={btn} onClick={() => runWhatIf(r.index_code)} disabled={busy}>
                          <TrendingUp className="w-3.5 h-3.5" /> Test
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && (
          <div className="rounded-xl border border-sky-200 dark:border-sky-500/25 bg-sky-50/60 dark:bg-sky-500/5 p-3">
            <p className="m-0 text-[11px] text-slate-600 dark:text-slate-300">
              <strong>How a price becomes a rate.</strong> A rate line does not say how much diesel it burns — you cannot get litres out of
              &ldquo;R450 a drop&rdquo;. It says what <strong>proportion of itself is diesel</strong>, and the rest is arithmetic:
              <span className="mx-1 font-mono text-slate-700 dark:text-slate-200">rate × (1 + share × (now ÷ base − 1))</span>.
              Diesel up 11% moves a per-km line at 40% share by 4.4%, a per-drop line at 20% by 2.2%, a per-pallet line at 10% by 1.1%.
              The rate basis stops mattering, which is why per-km, per-trip and per-unit can all sit behind one number.
            </p>
            <p className="m-0 mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              A share of <strong>0</strong> is a genuinely fixed-price contract and is immune — that is the default, so nothing escalates
              until you say it should. A share of <strong>100</strong> is a line that <em>is</em> the commodity and moves 1:1.
              The base each line escalates from is stamped on the <strong>rate line</strong>, not here: two carriers quoted six months apart
              must not share one base, or the later one is charged for a rise already inside its price. The <strong>Base</strong> column above
              is only what a <em>new</em> link is stamped with.
              Rates themselves are never rewritten — what you typed stays typed, and the escalation is applied when the cost is read.
            </p>
            {totals.linked > 0 && (
              <p className="m-0 mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                {totals.linked} rate line{totals.linked === 1 ? '' : 's'} across inter-depot and secondary currently move with an index,
                for {fmtR(totals.adjustment)} of escalation in this version.
                Inter-depot cost feeds landed cost, so a change here reprices product cost as well as distribution.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
